import type {StateCreator} from 'zustand'
import type {ClientDiffLine, DiffLine, DiffLineKey, DiffLineSelectionInfo, DiffOrientation} from '../../types'
import type {DiffAnchor, LineRange} from '@github-ui/diffs/types'
import DiffRowSelectedChecker from '../../helpers/diff-row-selected-checker'
import {groupDiffLines, isRetargetableLine} from '../../helpers/line-helpers'
import {
  getFirstValue,
  getLastValue,
  getSelectedLineKeys,
  getSelectedLineRangeFromUrl,
  isBeforePreviouslySelectedItem,
} from './helpers/diff-lines-slice-helpers'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {DiffSelectedLineEvent} from '../../util/custom-events'

const NO_LINE_NUMBER_VALUE = -1

export interface FirstLineSelectionOnDrag {
  lineNumber: number
  orientation: DiffOrientation
  shouldStartConversation: boolean
  lineKey: DiffLineKey
}

type LineSelectionCallBackFn = (args: {
  diffAnchor: DiffAnchor | string
  endLineNumber: number
  endOrientation: DiffOrientation
  startLineNumber: number
  startOrientation: DiffOrientation
}) => void

export interface DiffLinesSlice {
  diffLines: {
    _diffLines: DiffLine[]
    _diffLinesHash: Record<DiffLineKey, DiffLine>
    _firstSelectedLineInfo: {
      lineKey: DiffLineKey
      lineNumber: number
      orientation: DiffOrientation
    } | null
    diffAnchor: DiffAnchor
    fileLineCount: number
    firstLineSelectionOnDrag: FirstLineSelectionOnDrag | null
    isDraggingFromAddCommentButton: boolean
    allSelectedLinesAreRetargetable: () => boolean
    getDiffLinesInfo: () => DiffLine[]
    getSplitDiffLinesFromLineRange: (lineRange: LineRange) => {
      selectedLeftLines: ClientDiffLine[]
      selectedRightLines: ClientDiffLine[]
    }
    getfirstLineSelectionOnDrag: () => FirstLineSelectionOnDrag | null
    getIsDraggingFromAddCommentButton: () => boolean
    getSelectedDiffLines: () => DiffLine[]
    getSelectedLineRange: () => LineRange | undefined
    hasSelectedLines: () => boolean
    isSplitView: boolean
    selectedLineKeys: Set<DiffLineKey>
    selectedLinesInfo: Record<DiffLineKey, DiffLineSelectionInfo>
    selectedLineRange: LineRange | undefined
  }
  diffLinesActions: {
    addLineToSelection: (
      args: {lineKey: DiffLineKey | undefined; orientation: DiffOrientation},
      callbackFn: LineSelectionCallBackFn,
    ) => void
    clearFirstLineSelectionOnDrag: () => void
    clearSelectedLines: () => void
    setIsDraggingFromAddCommentButton: (isDragging: boolean) => void
    selectMultipleLines: (
      args: {
        endLineKey: DiffLineKey | undefined
        endOrientation: DiffOrientation
        startLineKey: DiffLineKey | undefined
        startOrientation: DiffOrientation
        firstLineSelectionOnDrag: FirstLineSelectionOnDrag
      },
      callbackFn: LineSelectionCallBackFn,
    ) => void
    selectLine: (
      args: {
        lineKey: DiffLineKey | undefined
        firstLineSelectionOnDrag: FirstLineSelectionOnDrag
      },
      callbackFn: LineSelectionCallBackFn,
    ) => void
  }
  diffLinesSliceUpdates: {
    updateDiffLinesHash: (updatedDiffLines: DiffLine[]) => void
    updateDiffView: (isSplitView: boolean) => void
  }
}

export type DiffLinesSliceOpts = {
  diffAnchor: DiffAnchor
  path: string
  diffLines: DiffLine[]
  fileLineCount: number
  isSplitView: boolean
}

export const createDiffLinesSlice =
  ({
    diffAnchor,
    path,
    diffLines: initDiffLines,
    fileLineCount,
    isSplitView: initSplitView,
  }: DiffLinesSliceOpts): StateCreator<DiffLinesSlice, [['zustand/devtools', never]]> =>
  (set, get) => {
    const initDiffLinesHash = initDiffLines.reduce(
      (acc, diffLine) => {
        if (!diffLine.key) return acc

        acc[diffLine.key] = diffLine
        return acc
      },
      {} as Record<DiffLineKey, DiffLine>,
    )

    const {
      selectedLineKeys: initSelectedLineKeys,
      selectedLineRange: initSelectedLineRange,
      startOrientation: initStartOrientation,
    } = getSelectedLineKeys({
      diffAnchor,
      diffLines: initDiffLines,
      isSplitView: initSplitView,
      lineRange: getSelectedLineRangeFromUrl(diffAnchor),
    })

    const initFirstSelectedKey = getFirstValue(initSelectedLineKeys)
    const initLastSelectedKey = getLastValue(initSelectedLineKeys)

    const initSelectedLinesInfo = initDiffLines.reduce(
      (acc, diffLine) => {
        const lineKey = diffLine.key
        if (lineKey && initSelectedLineKeys.has(lineKey)) {
          acc[lineKey] = {
            isSelected: true,
            isFirstSelected: initFirstSelectedKey === lineKey,
            isLastSelected: initLastSelectedKey === lineKey,
          }

          return acc
        }

        return acc
      },
      {} as Record<DiffLineKey, DiffLineSelectionInfo>,
    )

    let initfirstLineSelectionOnDrag = null

    if (initFirstSelectedKey) {
      const line = initDiffLinesHash[initFirstSelectedKey]
      if (line && line.key && initStartOrientation) {
        const lineNumber = initStartOrientation === 'left' ? line.left : line.right
        if (lineNumber) {
          initfirstLineSelectionOnDrag = {
            lineNumber,
            orientation: initStartOrientation,
            shouldStartConversation: false,
            lineKey: line.key,
          }
        }
      }
    }

    return {
      diffLines: {
        diffAnchor,
        _diffLines: initDiffLines,
        _diffLinesHash: initDiffLinesHash,
        selectedLinesInfo: initSelectedLinesInfo,
        fileLineCount,
        firstLineSelectionOnDrag: initfirstLineSelectionOnDrag,
        _firstSelectedLineInfo: initfirstLineSelectionOnDrag
          ? {
              lineKey: initfirstLineSelectionOnDrag.lineKey,
              lineNumber: initfirstLineSelectionOnDrag.lineNumber,
              orientation: initfirstLineSelectionOnDrag.orientation,
            }
          : null,
        allSelectedLinesAreRetargetable: () => {
          let hasNonRetargetableLine = false
          for (const lineKey of get().diffLines.selectedLineKeys) {
            const line = get().diffLines._diffLinesHash[lineKey]
            if (line && !isRetargetableLine(line)) {
              hasNonRetargetableLine = true
              break
            }
          }
          return !hasNonRetargetableLine
        },
        getDiffLinesInfo: () => get().diffLines._diffLines,
        getSplitDiffLinesFromLineRange: (lineRange: LineRange) => {
          const grouped = groupDiffLines(get().diffLines._diffLines)
          const leftLines = grouped.leftLines
          const rightLines = grouped.rightLines
          return DiffRowSelectedChecker({
            selectedLineRange: lineRange,
            leftLines,
            rightLines,
          })
        },
        getSelectedDiffLines: () => {
          const selectedDiffLines: DiffLine[] = []
          for (const lineKey of get().diffLines.selectedLineKeys) {
            const line = get().diffLines._diffLinesHash[lineKey]
            if (line) selectedDiffLines.push(line)
          }

          return selectedDiffLines
        },
        getfirstLineSelectionOnDrag: () => get().diffLines.firstLineSelectionOnDrag,
        getIsDraggingFromAddCommentButton: () => get().diffLines.isDraggingFromAddCommentButton,
        getSelectedLineRange: () => get().diffLines.selectedLineRange,
        hasSelectedLines: () => get().diffLines.selectedLineKeys.size > 0,
        isDraggingFromAddCommentButton: false,
        isSplitView: initSplitView,
        selectedLineKeys: initSelectedLineKeys,
        selectedLineRange: initSelectedLineRange,
      },
      diffLinesSliceUpdates: {
        updateDiffLinesHash: (updatedDiffLines: DiffLine[]) => {
          const updatedLinesToHash = updatedDiffLines.reduce(
            (acc, diffLine) => {
              if (!diffLine.key) return acc

              acc[diffLine.key] = diffLine
              return acc
            },
            {} as Record<DiffLineKey, DiffLine>,
          )

          set(
            prevState => ({
              diffLines: {
                ...prevState.diffLines,
                _diffLines: updatedDiffLines,
                _diffLinesHash: updatedLinesToHash,
              },
            }),
            undefined,
            'updateDiffLines',
          )
        },
        updateDiffView: (isSplitView: boolean) =>
          set(
            prevState => ({
              diffLines: {
                ...prevState.diffLines,
                isSplitView,
              },
            }),
            undefined,
            'update diff view settings',
          ),
      },
      diffLinesActions: {
        clearFirstLineSelectionOnDrag: () =>
          set(
            prevState => ({
              diffLines: {
                ...prevState.diffLines,
                firstLineSelectionOnDrag: null,
                isDraggingFromAddCommentButton: false,
              },
            }),
            undefined,
            'clearFirstLineSelectionOnDrag',
          ),

        setIsDraggingFromAddCommentButton: (isDragging: boolean) =>
          set(
            prevState => ({
              diffLines: {
                ...prevState.diffLines,
                isDraggingFromAddCommentButton: isDragging,
              },
            }),
            undefined,
            `setIsDraggingFromAddCommentButton:${isDragging}`,
          ),

        addLineToSelection: (
          {
            lineKey: addedLineKey,
            orientation: addedLineOrientation,
          }: {lineKey: DiffLineKey | undefined; orientation: DiffOrientation},
          callbackFn: LineSelectionCallBackFn,
        ) => {
          // Guard against running code when a addedLineKey is null
          if (!addedLineKey) return

          const lineToAdd = get().diffLines._diffLinesHash[addedLineKey]
          if (!lineToAdd) return

          const currentfirstLineSelectionOnDrag = get().diffLines.firstLineSelectionOnDrag

          const _firstSelectedLineInfo = get().diffLines._firstSelectedLineInfo
          const currentLineRange = get().diffLines.selectedLineRange
          // If the line to add is the first selected line number or if there is no current line range, we want to reset to a single line selection
          if (
            (currentfirstLineSelectionOnDrag && addedLineKey === currentfirstLineSelectionOnDrag.lineKey) ||
            !currentLineRange
          ) {
            const lineNumberToAdd = addedLineOrientation === 'right' ? lineToAdd.right : lineToAdd.left
            if (!lineNumberToAdd) return

            get().diffLinesActions.selectLine(
              {
                lineKey: addedLineKey,
                firstLineSelectionOnDrag: {
                  lineNumber: lineNumberToAdd,
                  orientation: addedLineOrientation,
                  lineKey: addedLineKey,
                  shouldStartConversation: false,
                },
              },
              callbackFn,
            )
            return
          }

          if (!_firstSelectedLineInfo) return

          const firstSelectedLine = get().diffLines._diffLinesHash[_firstSelectedLineInfo.lineKey]

          let newLineRange: LineRange

          if (
            isBeforePreviouslySelectedItem({
              current: lineToAdd,
              currentOrientation: addedLineOrientation,
              previous: firstSelectedLine,
              previousOrientation: _firstSelectedLineInfo.orientation,
            })
          ) {
            newLineRange = {
              ...currentLineRange,
              startLineNumber:
                (addedLineOrientation === 'left' ? lineToAdd?.left : lineToAdd?.right) ?? NO_LINE_NUMBER_VALUE,
              startOrientation: addedLineOrientation,
              endLineNumber: _firstSelectedLineInfo.lineNumber,
              endOrientation: _firstSelectedLineInfo.orientation,
            }
          } else {
            newLineRange = {
              ...currentLineRange,
              startLineNumber: _firstSelectedLineInfo.lineNumber,
              startOrientation: _firstSelectedLineInfo.orientation,
              endLineNumber:
                (addedLineOrientation === 'left' ? lineToAdd?.left : lineToAdd?.right) ?? NO_LINE_NUMBER_VALUE,
              endOrientation: addedLineOrientation,
            }
          }

          const {selectedLineKeys: newSelectedLineKeys, selectedLineRange: newSelectedLineRange} = getSelectedLineKeys({
            diffAnchor,
            diffLines: get().diffLines._diffLines,
            isSplitView: get().diffLines.isSplitView,
            lineRange: newLineRange,
          })

          const firstSelectedKey = getFirstValue(newSelectedLineKeys)
          const lastSelectedKey = getLastValue(newSelectedLineKeys)

          const newSelectedLinesInfo = Array.from(newSelectedLineKeys).reduce(
            (acc, lineKey) => {
              acc[lineKey] = {
                isFirstSelected: lineKey === firstSelectedKey,
                isLastSelected: lineKey === lastSelectedKey,
                isSelected: true,
              }
              return acc
            },
            {} as Record<DiffLineKey, DiffLineSelectionInfo>,
          )

          if (!newSelectedLineRange?.endLineNumber || !newSelectedLineRange?.startLineNumber) {
            return
          }
          const lineSelectionEvent = new DiffSelectedLineEvent(newSelectedLineRange, path)
          ssrSafeWindow?.dispatchEvent(lineSelectionEvent)

          set(
            prevState => ({
              diffLines: {
                ...prevState.diffLines,
                selectedLineKeys: newSelectedLineKeys,
                selectedLinesInfo: newSelectedLinesInfo,
                // // In case there is no current firstLineSelectionOnDrag, use the newly added line
                // firstLineSelectionOnDrag: get().diffLines.firstLineSelectionOnDrag,
                selectedLineRange: newSelectedLineRange,
              },
            }),
            undefined,
            'addLineToSelection',
          )

          callbackFn(newSelectedLineRange)
        },
        clearSelectedLines: () => {
          set(
            prevState => {
              return {
                diffLines: {
                  ...prevState.diffLines,
                  firstLineSelectionOnDrag: null,
                  selectedLineKeys: new Set([]),
                  selectedLinesInfo: {},
                  selectedLineRange: undefined,
                  _firstSelectedLineInfo: null,
                },
              }
            },
            undefined,
            'clearSelectedLines',
          )
        },
        selectLine: (
          {
            lineKey: selectedLineKey,
            firstLineSelectionOnDrag,
          }: {
            lineKey: DiffLineKey | undefined
            firstLineSelectionOnDrag: FirstLineSelectionOnDrag
          },
          callbackFn: LineSelectionCallBackFn,
        ) => {
          if (!selectedLineKey) return

          const diffLineSelected = get().diffLines._diffLinesHash[selectedLineKey]

          if (!diffLineSelected) return

          const newSelectedLinesInfo = {
            [selectedLineKey]: {
              isFirstSelected: true,
              isLastSelected: true,
              isSelected: true,
            },
          }

          const lineNumber =
            firstLineSelectionOnDrag.orientation === 'left' ? diffLineSelected.left : diffLineSelected.right
          if (!lineNumber) return

          const selectedLineRange = {
            diffAnchor: get().diffLines.diffAnchor,
            endLineNumber: lineNumber,
            endOrientation: firstLineSelectionOnDrag.orientation,
            startLineNumber: lineNumber,
            startOrientation: firstLineSelectionOnDrag.orientation,
          }

          const lineSelectionEvent = new DiffSelectedLineEvent(selectedLineRange, path)
          ssrSafeWindow?.dispatchEvent(lineSelectionEvent)
          set(
            prevState => ({
              diffLines: {
                ...prevState.diffLines,
                firstLineSelectionOnDrag: {
                  ...firstLineSelectionOnDrag,
                  lineKey: selectedLineKey,
                },
                selectedLineKeys: new Set([selectedLineKey]),
                selectedLinesInfo: newSelectedLinesInfo,
                selectedLineRange,
                _firstSelectedLineInfo: {
                  lineKey: selectedLineKey,
                  lineNumber: firstLineSelectionOnDrag.lineNumber,
                  orientation: firstLineSelectionOnDrag.orientation,
                },
              },
            }),
            undefined,
            'selectLine',
          )

          callbackFn(selectedLineRange)
        },
        selectMultipleLines: (
          {
            startLineKey,
            endLineKey,
            startOrientation,
            endOrientation,
            firstLineSelectionOnDrag,
          }: {
            endLineKey: DiffLineKey | undefined
            endOrientation: DiffOrientation
            startLineKey: DiffLineKey | undefined
            startOrientation: DiffOrientation
            firstLineSelectionOnDrag: FirstLineSelectionOnDrag
          },
          callbackFn: LineSelectionCallBackFn,
        ) => {
          // Guard against running code when a diffLineKey is null
          if (!startLineKey || !endLineKey) return
          if (!firstLineSelectionOnDrag) return

          const startLineToAdd = get().diffLines._diffLinesHash[startLineKey]
          const endLineToAdd = get().diffLines._diffLinesHash[endLineKey]

          // Return early if and don't set state if the first or last lines are missing a line number on left and right sides
          if (
            !startLineToAdd ||
            !endLineToAdd ||
            (!startLineToAdd.left && !startLineToAdd.right) ||
            (!endLineToAdd.left && !endLineToAdd.right)
          )
            return

          const tempLineRange: LineRange = {
            diffAnchor,
            endLineNumber:
              (endOrientation === 'right' ? endLineToAdd.right : endLineToAdd.left) ?? NO_LINE_NUMBER_VALUE,
            endOrientation,
            startLineNumber:
              (startOrientation === 'right' ? startLineToAdd.right : startLineToAdd.left) ?? NO_LINE_NUMBER_VALUE,
            startOrientation,
          }

          const {selectedLineKeys: newSelectedLineKeys, selectedLineRange: newSelectedLineRange} = getSelectedLineKeys({
            diffAnchor,
            diffLines: get().diffLines._diffLines,
            isSplitView: get().diffLines.isSplitView,
            lineRange: tempLineRange,
          })

          const firstSelectedKey = getFirstValue(newSelectedLineKeys)
          const lastSelectionKey = getLastValue(newSelectedLineKeys)

          const newSelectedLinesInfo = Array.from(newSelectedLineKeys).reduce(
            (acc, lineKey) => {
              acc[lineKey] = {
                isFirstSelected: lineKey === firstSelectedKey,
                isLastSelected: lineKey === lastSelectionKey,
                isSelected: true,
              }
              return acc
            },
            {} as Record<DiffLineKey, DiffLineSelectionInfo>,
          )

          if (!newSelectedLineRange?.endLineNumber || !newSelectedLineRange?.startLineNumber) {
            return
          }

          const lineSelectionEvent = new DiffSelectedLineEvent(newSelectedLineRange, path)
          ssrSafeWindow?.dispatchEvent(lineSelectionEvent)
          set(
            prevState => ({
              diffLines: {
                ...prevState.diffLines,
                selectedLinesInfo: newSelectedLinesInfo,
                selectedLineKeys: newSelectedLineKeys,
                firstLineSelectionOnDrag,
                selectedLineRange: newSelectedLineRange,
                _firstSelectedLineInfo: {
                  lineNumber: firstLineSelectionOnDrag.lineNumber,
                  orientation: firstLineSelectionOnDrag.orientation,
                  lineKey: firstLineSelectionOnDrag.lineKey,
                },
              },
            }),
            undefined,
            'selectMultipleLines',
          )

          callbackFn(newSelectedLineRange)
        },
      },
    }
  }
